import React, { useState } from 'react'
import { ChevronDown, ChevronUp, Sparkles, TrendingDown, TrendingUp } from 'lucide-react'
import { Bar, Cell, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis } from 'recharts'
import PerformanceDashboardService from '../../../../services/dashboards/PerformanceDashboard.service'
import KpiHelpIcon, { type KpiTooltipKey } from './KpiHelpIcon'
import { cn } from '../../../../lib/utils'
import { requestOrbitIndiePanelOpen } from '../../../../services/orbit/orbitPageAssistantBridge'

type DeltaTone = 'success' | 'destructive' | 'info'

type MetricTrendPoint = {
  month: string;
  current: number;
  comparison: number;
}

export type PerformanceMetricCard = {
  label: string;
  icon: React.ElementType;
  value: string;
  delta: string;
  deltaDollar: string;
  tone: DeltaTone;
  ly: string;
  trend: MetricTrendPoint[];
  mtdTrend?: MetricTrendPoint[];
  trendLabel: string;
  planComparison: string;
  planComparisonDollar: string;
  lastYearComparison: string;
  lastYearComparisonDollar: string;
}

export type PerformancePeriod = 'MTD' | 'Last Month' | 'Last 3M' | 'Last 12M'

export type TransactionalPrimaryCard = {
  label: string;
  icon: React.ElementType;
  value: string;
  delta: string;
  comparison: string;
  deltaClassName: string;
  trend: Array<{ month: string; value: number }>;
}

export type TransactionalCompactCard = {
  label: string;
  icon: React.ElementType;
  value: string;
  delta: string;
  deltaClassName: string;
}

const toneClasses: Record<DeltaTone, string> = {
  success: 'bg-[#ECFDF5] text-[#027A48]',
  destructive: 'bg-[#FEF3F2] text-[#B42318]',
  info: 'bg-[#F4F3FF] text-[#5925DC]',
}

function KeyMetricMonthlyBarChart({ label, points }: { label: string; points: MetricTrendPoint[] }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        aria-label={`${label} monthly actuals vs plan`}
        data={points}
        margin={{ top: 12, right: 8, left: 8, bottom: 0 }}
        barCategoryGap="14%"
        onMouseMove={(state) => {
          if (state && state.activeTooltipIndex !== undefined) {
            setActiveIndex(state.activeTooltipIndex)
          }
        }}
        onMouseLeave={() => setActiveIndex(null)}
      >
        <XAxis dataKey="month" axisLine={{ stroke: '#EAECF0' }} tickLine={false} tick={{ fill: '#667085', fontSize: 11 }} tickMargin={8} />
        <Tooltip
          cursor={{ fill: 'transparent' }}
          content={({ active, payload, label: xLabel }) => {
            if (!active || !payload || !payload.length) return null
            const actualVal = payload.find((p) => p.dataKey === 'current')?.value
            const planVal = payload.find((p) => p.dataKey === 'comparison')?.value
            return (
              <div className="rounded-xl bg-[#101828] px-3.5 py-2 text-xs text-white shadow-2xl">
                <div className="text-[11px] font-medium text-[#98A2B3]">{xLabel}</div>
                <div className="mt-1 flex items-center gap-2 font-medium">
                  <span className="h-2 w-2 rounded-full bg-[#B692F6]" />
                  <span>Actual: {PerformanceDashboardService.moneyFormat(Number(actualVal) || 0, true)}</span>
                </div>
                {planVal !== undefined && (
                  <div className="mt-0.5 flex items-center gap-2 font-medium">
                    <span className="h-2 w-2 rounded-full bg-[#F04438]" />
                    <span>Plan: {PerformanceDashboardService.moneyFormat(Number(planVal) || 0, true)}</span>
                  </div>
                )}
              </div>
            )
          }}
        />
        <Bar dataKey="current" radius={[4, 4, 0, 0]}>
          {points.map((_, index) => {
            const isHighlighted = activeIndex !== null ? index === activeIndex : index === Math.max(points.length - 6, 0)
            return (
              <Cell key={`cell-${index}`} fill={isHighlighted ? '#B692F6' : '#F4F3FF'} />
            )
          })}
        </Bar>
        <Line type="monotone" dataKey="comparison" stroke="#F04438" strokeWidth={2.5} dot={false} />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

function SelectedKeyMetricDetail({
  metric,
  activePeriod,
  showMode,
  trendLoading,
}: {
  metric: PerformanceMetricCard;
  activePeriod: PerformancePeriod;
  showMode: 'dollar' | 'percent';
  trendLoading: boolean;
}) {
  const Icon = metric.icon
  const isPositive = !metric.delta.includes('-')
  const displayDelta = formatDeltaDisplay(metric.deltaDollar, metric.delta, showMode)
  const displayPlanComp = formatDeltaDisplay(metric.planComparisonDollar, metric.planComparison, showMode)
  const displayLYComp = formatDeltaDisplay(metric.lastYearComparisonDollar, metric.lastYearComparison, showMode)

  const trendTitle =
    activePeriod === 'Last 3M'
      ? '3-MONTH TREND'
      : activePeriod === 'Last 12M'
        ? '12-MONTH TREND'
        : activePeriod === 'Last Month'
          ? 'LAST MONTH TREND'
          : 'MTD TREND'

  const filteredPoints = React.useMemo(() => {
    // Checked before the metric.trend emptiness guard so a client with no completed history yet still shows its MTD point.
    if (activePeriod === 'MTD') return metric.mtdTrend && metric.mtdTrend.length > 0 ? metric.mtdTrend : []
    if (!metric.trend || metric.trend.length === 0) return []
    if (activePeriod === 'Last 3M') return metric.trend.slice(-3)
    if (activePeriod === 'Last Month') return metric.trend.slice(-1)
    return metric.trend
  }, [metric.trend, metric.mtdTrend, activePeriod])

  return (
    <section aria-label={`${metric.label} expanded details`} className="rounded-[20px] border border-[#EAECF0] bg-white p-6 shadow-xs">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start">
        <div>
          <div className="flex items-center gap-2 text-[12px] font-medium text-[#667085]">
            <Icon className="h-5 w-5 text-[#667085]" />
            {metric.label}
          </div>
          <div className="mt-2.5 text-[30px] font-semibold tracking-tight text-[#101828]">{metric.value}</div>
          <div className="mt-2.5 flex items-center gap-2">
            <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold', toneClasses[metric.tone])}>
              {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {displayDelta}
            </span>
          </div>
          <p className="mt-3.5 text-xs font-medium text-[#667085]">{metric.ly}</p>

          <div className="mt-5 rounded-[16px] border border-[#EAECF0] bg-[#F9FAFB] p-4 text-xs leading-relaxed text-[#344054]">
            <strong className="font-bold text-[#101828]">Why it matters: </strong>
            {metric.label} is your topline health check — the foundation every other metric connects back to.
          </div>

          <div className="mt-3.5 rounded-[20px] bg-[#EEEDFE] p-4">
            <div className="flex items-center gap-1.5 text-xs font-bold text-[#534AB7]">
              <Sparkles className="h-4 w-4" />
              Indie insight
            </div>
            <p className="mt-2 text-sm font-medium leading-relaxed text-[#344054]">
              {metric.label} has climbed for 4 straight months and sits well above plan.
            </p>
            <button
              type="button"
              onClick={() => requestOrbitIndiePanelOpen({
                source: 'key-metrics',
                prompt: `What's driving ${metric.label}?`,
                submit: true,
              })}
              className="mt-2 inline-flex items-center gap-1 rounded-full border border-[#D6BBFB] bg-white px-4 py-2 text-xs font-bold text-[#534AB7] shadow-2xs transition-colors hover:bg-[#F4F3FF]"
            >
              Ask Indie &quot;What&apos;s driving this?&quot;
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3.5">
          <div className="rounded-[16px] border border-[#EAECF0] bg-white p-4 shadow-2xs">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#667085]">{trendTitle}</p>
              <div className="flex items-center gap-1.5 text-xs font-medium text-[#667085]">
                <span className="h-0.5 w-3.5 bg-[#F04438]" />
                Plan
              </div>
            </div>

            <div className="mt-3 h-48">
              {trendLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-[#667085]">Loading historical KPI data…</div>
              ) : filteredPoints.length > 0 ? (
                <KeyMetricMonthlyBarChart label={metric.label} points={filteredPoints} />
              ) : (
                <div className="flex h-full items-center justify-center text-center text-sm text-[#667085]">
                  No historical KPI values are available for this period.
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3.5">
            <div className="rounded-[16px] border border-[#EAECF0] bg-[#F9FAFB] p-3.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#98A2B3]">PLAN</p>
              <div className="mt-2 flex items-center gap-1">
                <TrendingUp className="h-4 w-4 shrink-0 text-[#027A48]" />
                <span className="text-base font-bold text-[#101828]">{displayPlanComp}</span>
                {displayPlanComp !== 'Plan unavailable' && (
                  <span className="text-xs font-normal text-[#667085]">vs Plan</span>
                )}
              </div>
            </div>

            <div className="rounded-[16px] border border-[#EAECF0] bg-[#F9FAFB] p-3.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#98A2B3]">LAST YEAR</p>
              <div className="mt-2 flex items-center gap-1">
                <TrendingUp className="h-4 w-4 shrink-0 text-[#027A48]" />
                <span className="text-base font-bold text-[#101828]">{displayLYComp}</span>
                {displayLYComp !== 'LY unavailable' && (
                  <span className="text-xs font-normal text-[#667085]">vs LY</span>
                )}
              </div>
            </div>

            <div className="rounded-[16px] border border-[#EAECF0] bg-[#F9FAFB] p-3.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#98A2B3]">PEERS</p>
              <div className="mt-2 flex items-center gap-1">
                <TrendingUp className="h-4 w-4 shrink-0 text-[#027A48]" />
                <span className="text-base font-bold text-[#101828]">+6.0%</span>
                <span className="text-xs font-normal text-[#667085]">vs Peers</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function mapLabelToTooltipKey(label: string): KpiTooltipKey {
  const normalized = label.toLowerCase().trim()
  if (normalized.includes('sales')) return 'sales'
  if (normalized.includes('inventory')) return 'inventoryAtRetail'
  if (normalized.includes('turn')) return 'annualTurn'
  if (normalized.includes('markdown')) return 'markdowns'
  if (normalized.includes('inflow')) return 'inflow'
  if (normalized.includes('gross margin')) return 'grossMargin'
  if (normalized.includes('cash margin')) return 'cashMargin'
  return 'sales'
}

// SPS-5171: buildPerformanceKeyMetrics now precomputes a real dollar AND percent string for each of
// these three fields, so displaying the right one for the active $/% toggle is just a lookup - no more
// guessing a percentage out of a dollar string's digits (or vice versa) with a regex.
function formatDeltaDisplay(dollarValue: string, percentValue: string, showMode: 'dollar' | 'percent'): string {
  return showMode === 'dollar' ? dollarValue : percentValue
}

export function KeyMetricsSection({
  metrics,
  selectedMetric,
  selectedLabel,
  onSelectedLabelChange,
  activePeriod,
  onActivePeriodChange,
  loading,
  trendLoading,
}: {
  metrics: PerformanceMetricCard[];
  selectedMetric: PerformanceMetricCard | null;
  selectedLabel: string | null;
  onSelectedLabelChange: (label: string | null) => void;
  activePeriod: PerformancePeriod;
  onActivePeriodChange: (period: PerformancePeriod) => void;
  loading: boolean;
  trendLoading: boolean;
}) {
  const periods: PerformancePeriod[] = ['MTD', 'Last Month', 'Last 3M', 'Last 12M']
  const [showMode, setShowMode] = useState<'dollar' | 'percent'>('dollar')

  return (
    <section className="flex flex-col gap-3.5 pt-2">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <p className="text-[12px] font-semibold uppercase tracking-wider text-[#667085]">
          KEY METRICS
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 rounded-full border border-[#EAECF0] bg-white p-1 shadow-2xs">
            <span className="ml-1 mr-0.5 text-xs font-normal text-[#667085]">Show</span>
            <button
              type="button"
              onClick={() => setShowMode('percent')}
              className={cn(
                'rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors cursor-pointer',
                showMode === 'percent' ? 'bg-[#F4F3FF] text-[#5925DC]' : 'text-[#667085] hover:text-[#344054]'
              )}
            >
              %
            </button>
            <button
              type="button"
              onClick={() => setShowMode('dollar')}
              className={cn(
                'rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors cursor-pointer',
                showMode === 'dollar' ? 'bg-[#F4F3FF] text-[#5925DC]' : 'text-[#667085] hover:text-[#344054]'
              )}
            >
              $
            </button>
          </div>

          <div className="flex items-center gap-1 rounded-full border border-[#EAECF0] bg-white p-1 shadow-2xs">
            {periods.map((period) => (
              <button
                key={period}
                type="button"
                onClick={() => onActivePeriodChange(period)}
                className={cn(
                  'rounded-full px-3 py-1 text-xs transition-colors cursor-pointer',
                  activePeriod === period ? 'bg-[#F4F3FF] font-bold text-[#5925DC]' : 'font-medium text-[#667085] hover:text-[#344054]'
                )}
              >
                {period}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-5">
        {loading ? (
          <div className="rounded-[16px] border border-[#EAECF0] bg-white p-4 text-sm text-[#667085] shadow-xs">
            Loading key metrics…
          </div>
        ) : (
          metrics.map((metric) => {
            const isSelected = selectedLabel === metric.label
            const Icon = metric.icon
            const isPositive = !metric.delta.includes('-')
            const displayDelta = formatDeltaDisplay(metric.deltaDollar, metric.delta, showMode)

            return (
              <button
                key={metric.label}
                type="button"
                aria-pressed={isSelected}
                aria-expanded={isSelected}
                aria-controls={`key-metric-${metric.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-details`}
                data-state={isSelected ? 'selected' : 'collapsed'}
                onClick={() => onSelectedLabelChange(isSelected ? null : metric.label)}
                style={{
                  borderRadius: '14px',
                  border: isSelected ? '2px solid #9E77ED' : '1px solid rgba(0, 0, 0, 0.10)',
                  background: '#FFF',
                  boxShadow: '0 1px 2px -1px rgba(0, 0, 0, 0.10), 0 1px 3px 0 rgba(0, 0, 0, 0.10)',
                }}
                className={cn(
                  'flex min-w-0 flex-col p-4 text-left transition-all cursor-pointer',
                  isSelected && 'border-2 border-[#9E77ED]'
                )}
              >
                <div className="flex items-center justify-between gap-1.5">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Icon className="h-4 w-4 shrink-0 text-[#98A2B3]" />
                    <span className="truncate text-[12px] font-medium text-[#667085]">{metric.label}</span>
                    <KpiHelpIcon tooltipKey={mapLabelToTooltipKey(metric.label)} />
                  </div>
                  {isSelected ? (
                    <ChevronUp aria-hidden="true" className="h-4 w-4 shrink-0 text-[#7F56D9]" />
                  ) : (
                    <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0 text-[#98A2B3]" />
                  )}
                </div>

                <div className="mt-2.5 text-[20px] font-semibold tracking-tight text-[#101828] leading-snug">{metric.value}</div>

                <div className="mt-2 flex items-center gap-1.5">
                  <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none', toneClasses[metric.tone])}>
                    {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {displayDelta}
                  </span>
                </div>

                <div className="mt-[10px] text-[12px] font-medium text-[#667085]">{metric.ly}</div>
              </button>
            )
          })
        )}
      </div>

      {selectedMetric && (
        <div id={`key-metric-${selectedMetric.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-details`}>
          <SelectedKeyMetricDetail metric={selectedMetric} activePeriod={activePeriod} showMode={showMode} trendLoading={trendLoading} />
        </div>
      )}
    </section>
  )
}



